import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/services/apiClient';
import PageHeader from '@/components/ui/PageHeader';
import Card from '@/components/ui/Card';
import Spinner from '@/components/ui/Spinner';
import DataTable from '@/components/ui/DataTable';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import { fmtMoney } from '@/lib/enums';

const money = (n) =>
  n == null ? '—' : `R$ ${Number(n).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}`;
const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('pt-BR'));

const TIER_META = {
  starter: { label: 'Starter', ai: false },
  pro: { label: 'Pro', ai: false, popular: true },
  elite: { label: 'Elite', ai: false },
  ai: { label: 'Movez AI', ai: true },
};

function PlanCard({ plan, active, t }) {
  const accent = plan.ai ? 'var(--frost)' : 'var(--volt)';
  const border = plan.popular
    ? 'color-mix(in srgb, var(--volt) 40%, transparent)'
    : plan.ai
      ? 'color-mix(in srgb, var(--frost) 35%, transparent)'
      : 'var(--line)';
  return (
    <Card style={{ padding: 18, borderColor: border, position: 'relative' }}>
      {plan.popular && (
        <span
          style={{
            position: 'absolute',
            top: 14,
            right: 14,
            fontFamily: 'var(--font-mono)',
            fontSize: 8,
            fontWeight: 600,
            letterSpacing: '0.1em',
            background: 'var(--volt)',
            color: 'var(--ink)',
            padding: '3px 7px',
            borderRadius: 'var(--radius-pill)',
          }}
        >
          {t('finance.popular')}
        </span>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        {plan.ai && (
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--frost)' }} />
        )}
        <span
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 600,
            fontSize: 17,
            color: plan.ai ? 'var(--frost)' : 'var(--hi)',
          }}
        >
          {plan.name}
        </span>
      </div>
      <div style={{ color: 'var(--mid)', fontSize: 12, marginTop: 4 }}>{plan.sub}</div>
      <div
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 700,
          fontSize: 26,
          color: plan.ai ? 'var(--frost)' : 'var(--hi)',
          marginTop: 14,
        }}
      >
        {money(plan.priceMonthly)}
        <span style={{ fontSize: 13, color: 'var(--mid)', fontWeight: 500 }}>
          /{t('finance.perMonth')}
        </span>
      </div>
      <div style={{ height: 1, background: 'var(--line)', margin: '14px 0' }} />
      <div style={{ fontSize: 12.5, color: 'var(--mid)' }}>
        {t('finance.active')}{' '}
        <strong style={{ color: accent }}>{fmt(active)}</strong>
      </div>
    </Card>
  );
}

function Kpi({ label, value, tone = 'hi' }) {
  return (
    <Card style={{ padding: 16 }}>
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 10,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: 'var(--low)',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 700,
          fontSize: 26,
          color: tone === 'frost' ? 'var(--frost)' : 'var(--hi)',
          marginTop: 8,
        }}
      >
        {value}
      </div>
    </Card>
  );
}

export default function AdminFinance() {
  const { t } = useTranslation();
  const [catalog, setCatalog] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commission, setCommission] = useState(''); // em % (ex.: "15")
  const [savingCommission, setSavingCommission] = useState(false);
  const [commissionMsg, setCommissionMsg] = useState('');

  useEffect(() => {
    Promise.all([api.get('/subscriptions/plans'), api.get('/metrics/admin'), api.get('/admin/settings')])
      .then(([plansRes, mRes, sRes]) => {
        setCatalog(plansRes.data || plansRes);
        setMetrics(mRes.data || mRes);
        const pct = Number((sRes.data || sRes).commissionPct);
        if (Number.isFinite(pct)) setCommission(String(Math.round(pct * 1000) / 10));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const saveCommission = async () => {
    const pct = Number(String(commission).replace(',', '.'));
    setCommissionMsg('');
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      setCommissionMsg(t('finance.commissionInvalid'));
      return;
    }
    setSavingCommission(true);
    try {
      await api.put('/admin/settings', { commissionPct: pct / 100 });
      setCommissionMsg(t('finance.commissionSaved'));
    } catch (e) {
      setCommissionMsg(e.message);
    } finally {
      setSavingCommission(false);
    }
  };

  // Monta os 4 cards a partir do catálogo (saas[] + ai) com contagem real por tier.
  const byTier = metrics?.subscriptionsByTier || {};
  const plans = [];
  for (const p of catalog?.saas || []) {
    const meta = TIER_META[p.tier] || { label: p.tier, ai: false };
    plans.push({
      tier: p.tier,
      name: meta.label,
      ai: meta.ai,
      popular: meta.popular,
      priceMonthly: p.priceMonthly,
      sub:
        p.maxStudents == null
          ? t('finance.unlimitedStudents')
          : t('finance.upToStudents', { count: p.maxStudents }),
      active: byTier[p.tier] ?? 0,
    });
  }
  if (catalog?.ai) {
    plans.push({
      tier: 'ai',
      name: TIER_META.ai.label,
      ai: true,
      priceMonthly: catalog.ai.priceMonthly,
      sub: t('finance.aiForStudents'),
      active: byTier.ai ?? 0,
    });
  }

  const saasActive =
    (byTier.starter ?? 0) + (byTier.pro ?? 0) + (byTier.elite ?? 0);

  return (
    <>
      <PageHeader kicker="Movez · Admin" title={t('nav.finance')} />

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
          <Spinner />
        </div>
      ) : (
        <>
          <div className="kicker" style={{ marginBottom: 12 }}>
            {t('finance.saasPlans')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
            {plans.map((p) => (
              <PlanCard key={p.tier} plan={p} active={p.active} t={t} />
            ))}
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 14,
              marginTop: 'var(--space-6)',
            }}
          >
            <Kpi label={t('finance.mrr')} value={money(metrics?.mrr)} />
            <Kpi label={t('finance.activeSaas')} value={fmt(saasActive)} />
            <Kpi label={t('finance.activeAi')} value={fmt(byTier.ai ?? 0)} tone="frost" />
          </div>

          {/* Comissão do marketplace (configurável; compras antigas mantêm o % da época) */}
          <Card style={{ padding: 16, marginTop: 'var(--space-6)' }}>
            <div className="kicker" style={{ marginBottom: 10 }}>{t('finance.commissionSetting')}</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
              <Input
                label={t('finance.commissionLabel')}
                inputMode="decimal"
                value={commission}
                onChange={(e) => setCommission(e.target.value)}
                style={{ maxWidth: 120 }}
              />
              <Button loading={savingCommission} onClick={saveCommission}>{t('common.save')}</Button>
              {commissionMsg && <span style={{ color: 'var(--mid)', fontSize: 13, paddingBottom: 12 }}>{commissionMsg}</span>}
            </div>
            <p style={{ color: 'var(--mid)', fontSize: 12.5, marginTop: 10 }}>{t('finance.commissionHint')}</p>
          </Card>

          {/* Quanto a plataforma ganha por pacote (comissões do mês corrente) */}
          {metrics?.commissionByPackage?.length > 0 && (
            <>
              <div className="kicker" style={{ margin: '32px 0 12px' }}>
                {t('finance.commissionByPackage')}
              </div>
              <DataTable
                columns={[
                  { key: 'title', header: t('finance.colPackage'), render: (r) => <strong style={{ color: 'var(--hi)' }}>{r.title}</strong> },
                  { key: 'trainerName', header: t('finance.colTrainer'), render: (r) => <span style={{ color: 'var(--mid)' }}>{r.trainerName}</span> },
                  { key: 'sales', header: t('finance.colSales'), render: (r) => fmt(r.sales) },
                  { key: 'gross', header: t('finance.colGross'), render: (r) => fmtMoney(r.gross) },
                  { key: 'commission', header: t('finance.colCommission'), align: 'right', render: (r) => <strong style={{ color: 'var(--volt)' }}>{fmtMoney(r.commission)}</strong> },
                ]}
                rows={metrics.commissionByPackage}
              />
            </>
          )}
        </>
      )}
    </>
  );
}
